import Alpine from 'alpinejs'
import { fragment, uuid } from '@js/util/helpers.js'

// Define (mobile) menu open/close state
Alpine.store('menu', false)

// The dropdown icon, as a DocumentFragment
let dropdownSvg = fragment`<svg aria-hidden="true" focusable="false" width="9" height="6" viewBox="0 0 9 6" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 1.3 4.5 5.74 0 1.29 1.05.25 4.5 3.66 7.95.25 9 1.29Z" fill="#000"/></svg>`

// Use @media query for hovering devices
let canHover = window.matchMedia('(hover: hover) and (pointer: fine)')
Alpine.store('canHover', () => canHover.matches)

// Define transition options for menu animations
Alpine.store('menuTransitions', {
  // The whole mobile menu when toggled
  mobileMenuContainer: {
    css: {
      opacity: 0,
      transform: 'translateY(-5px)',
    },
    duration: 300,

    // Visuals controlled by the global 'menu' state
    bind: 'menu',
  },
  mobileMenuOverlay: {
    css: {
      opacity: 0,
    },
    duration: 400,

    // Visuals controlled by the global 'menu' state
    bind: 'menu',
  },

  // A submenu expanding/collapsing on mobile
  mobileSubmenu: {
    css: {
      height: 0,
      marginTop: 0,
      marginBottom: 0,
      paddingTop: 0,
      paddingBottom: 0,
      opacity: 0,
    },
    duration: 300,

    // Hide overflow during animation to properly visualize height transition
    onBeforeAnimate(element) {
      element.style.overflow = 'hidden'
    },
    onComplete(element) {
      element.style.removeProperty('overflow')
    },
  },

  // A submenu appearing/disappearing on desktop
  desktopSubmenu: {
    css: {
      opacity: 0,
      transform: 'translateY(-20px)',
    },
    duration: 200,

    // Avoid pointer interactivity to avoid accidental closing/re-opening during transition
    onBeforeAnimate(element) {
      element.style.pointerEvents = 'none'
    },
    onComplete(element) {
      element.style.removeProperty('pointer-events')
    },
  },
})

// We'll attach the Alpine menu logic via JS because it would be *very* tedious
// to do so via the various hooks WordPress provides for menu HTML generation.
// A non-JS fallback is ensured through pure CSS.
document.querySelectorAll('nav.main').forEach(menuNavContainer => {
  let isMobile = menuNavContainer.matches('.mobile-menu-wrapper *')

  menuNavContainer.querySelectorAll('.menu-item a').forEach(menuItemLink => {
    if (
      isMobile &&
      menuItemLink.getAttribute('href') &&
      menuItemLink.getAttribute('href') !== '#' &&
      menuItemLink.getAttribute('href').startsWith('#')
    ) {
      menuItemLink.setAttribute('x-on:click', '$store.menu = false')
    }
  })

  // Iterate all list items that have submenus
  menuNavContainer.querySelectorAll('.menu-item-has-children').forEach(menuItem => {
    let submenuList = menuItem.querySelector('.sub-menu')
    let menuItemLink = menuItem.querySelector('a')

    // If no href is set, set it to # to make it focusable
    if (!menuItemLink.getAttribute('href')) {
      menuItemLink.setAttribute('href', '#')
    }

    submenuList.setAttribute('x-bind', 'target')

    // Make submenu focusable as a whole and hide it
    submenuList.setAttribute('tabindex', '-1')

    if (isMobile) {
      /*******************************
       * Mobile menu specific functionality
       ******************************/

      // Hide submenu unless it's the current menu item
      if (!menuItem.classList.contains('current-menu-parent')) {
        submenuList.style.display = 'none'
      }

      // Add unique ID to submenu for ARIA markup
      let menuItemIdClass = [...menuItem.classList].find(className =>
        /^menu-item-[0-9]+$/.test(className),
      )
      let submenuId = menuItemIdClass
        ? `submenu-${menuItemIdClass.replace('menu-item-', '')}`
        : `submenu-${uuid()}`

      submenuList.setAttribute('id', submenuId)

      // Disable link clickability if it points to empty anchor
      // Make it show the submenu instead
      if (menuItemLink.getAttribute('href') === '#') {
        menuItemLink.setAttribute('x-on:click.prevent', 'toggle()')
      }

      menuItem.setAttribute('x-data', 'transition($store.menuTransitions.mobileSubmenu)')

      // Insert dropdown icon after link
      menuItemLink.parentNode.insertBefore(
        fragment`
          <button type="button" class="dropdown" @click="toggle()" :aria-expanded="isVisible()" :aria-controls="${JSON.stringify(
            submenuId,
          )}">
          ${dropdownSvg.cloneNode(true)}
          </button>
        `,
        menuItemLink.nextSibling,
      )
    } else {
      /*******************************
       * Desktop menu specific functionality
       ******************************/

      submenuList.style.display = 'none'

      // Disable link clickability if it points to empty anchor
      // Make it focus the submenu instead, this will ensure that touch devices can open
      // the submenu through the dopdrown button
      if (menuItemLink.getAttribute('href') === '#') {
        menuItemLink.setAttribute(
          'x-on:click.prevent',
          `$el.parentNode.querySelector('ul').focus()`,
        )
      }

      menuItem.setAttribute('x-data', 'transition($store.menuTransitions.desktopSubmenu)')

      // Insert dropdown button after link
      menuItemLink.parentNode.insertBefore(
        fragment`
          <button type="button" class="dropdown" @click="$el.parentNode.querySelector('ul').focus()" tabindex="-1">
            ${dropdownSvg.cloneNode(true)}
          </button>
        `,
        menuItemLink.nextSibling,
      )

      // Toggle menu on mouseenter/mouseleave for mouse navigation
      menuItem.setAttribute(':style', '{ zIndex: latestEl === $el ? zIndex : null }')
      menuItem.setAttribute(
        'x-on:mouseenter',
        `zIndex++; latestEl = $el; $store.canHover() && show()`,
      )
      menuItem.setAttribute(
        'x-on:mouseleave',
        `$store.canHover() && !isAnimating($el) && hide({ delay: 250 })`,
      )

      // Toggle menu on focusin/focusout for keyboard navigation
      menuItem.setAttribute(
        'x-on:focusin',
        `
        (
          // Dropdown click
          $event.target.matches('button, button *') ||

          // Keyboard focus
          document.documentElement.classList.contains('no-focus-visible') ||
          $el.querySelector('a').matches(':focus-visible')
        ) &&

        show()
        `,
      )
      menuItem.setAttribute(
        'x-on:focusout',
        `setTimeout(() => {
          // Timeout is needed because in the moment the event occurs, there is no focused element
          !isAnimating($el) && !$el.querySelector(':focus') && hide()
        }, 0)`,
      )
    }

    menuItem.setAttribute(':class', `isVisible() ? 'rotate-dropdown-arrow' : null`)
  })
})
